import { z } from 'zod';
import type { FeeAuthorization } from '~/features/employer/billing/types';
import { MARKET_ACTION_TIMEOUT_MS, MARKET_READ_TIMEOUT_MS } from '~/features/market/requestPolicy';
import { JobMessageSchema } from '~/features/provider-hlx/job-opportunities/schemas';
import type { OpportunityFilter } from '~/features/provider-hlx/job-opportunities/types';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { getData, patchData, postData } from '~/utils/service';
import {
  ServiceCounterAvailabilitySchema,
  ServiceCounterInputSchema,
  ServiceOpportunitiesPageSchema,
  ServiceOpportunitySchema,
  type ServiceOpportunitiesPage,
  type ServiceOpportunity,
} from './schemas';

// The API caps pageSize at 100. MAX_INBOX_PAGES bounds one read; pages after
// the first load INBOX_PAGE_CONCURRENCY at a time.
const INBOX_PAGE_SIZE = 100;
const MAX_INBOX_PAGES = 50;
const INBOX_PAGE_CONCURRENCY = 4;

export type ServiceInbox = { data: ServiceOpportunity[]; total: number; truncated: boolean };

// The API orders the inbox by updatedAt, then responseId, both descending.
const ranksAfter = (row: ServiceOpportunity, other: ServiceOpportunity) => {
  const a = Date.parse(row.updatedAt);
  const b = Date.parse(other.updatedAt);
  return a !== b ? a < b : row.responseId < other.responseId;
};

export const SERVICE_OPPORTUNITIES_API = {
  getCounterAvailability: async (
    responseId: number,
    expectedNegotiationId: number,
    window: { start: string; end: string },
    signal?: AbortSignal
  ) => {
    const id = z.number().int().positive().parse(responseId);
    const expected = z.number().int().positive().parse(expectedNegotiationId);
    const range = z
      .object({ start: z.iso.date(), end: z.iso.date() })
      .refine((value) => value.start <= value.end)
      .parse(window);
    return withRequestDeadline(
      async (requestSignal) => {
        const selection = ServiceCounterAvailabilitySchema.parse(
          await getData('serviceOpportunityCounterAvailability', {
            pathParams: { responseId: String(id) },
            params: { expectedNegotiationId: String(expected), ...range },
            signal: requestSignal,
          })
        );
        if (
          selection.responseId !== id ||
          selection.expectedNegotiationId !== expected ||
          selection.entries.some((entry) => entry.date < range.start || entry.date > range.end)
        )
          throw new Error('Counter selection scope changed.');
        return selection;
      },
      MARKET_READ_TIMEOUT_MS,
      signal
    );
  },

  counter: async (responseId: number, input: unknown, signal?: AbortSignal) => {
    const id = z.number().int().positive().parse(responseId);
    // Parsing copies the complete reviewed payload before authentication can yield.
    const payload = ServiceCounterInputSchema.parse(input);
    return withRequestDeadline(
      async (requestSignal) => {
        const receipt = ServiceOpportunitySchema.parse(
          await postData('serviceOpportunityCounter', {
            pathParams: { responseId: String(id) },
            // [MCV-1850] The receipt replaces the row, so it must carry the owner's Decline too.
            params: { allowReject: 'true' },
            data: payload,
            signal: requestSignal,
          })
        );
        if (
          receipt.responseId !== id ||
          receipt.status !== 'NEGOTIATING' ||
          !receipt.latestNegotiation ||
          receipt.latestNegotiation.id === payload.expectedNegotiationId ||
          receipt.latestNegotiation.selectionStatus !== 'RECORDED'
        )
          throw new Error('Counter acknowledgement could not be verified.');
        return receipt;
      },
      MARKET_ACTION_TIMEOUT_MS,
      signal
    );
  },

  getPage: async (
    filter: OpportunityFilter | 'history',
    page = 1,
    signal?: AbortSignal,
    providerServiceListingId?: number,
    pageSize = 20,
    includeDeclined = false
  ) => {
    const listingId = z.number().int().positive().optional().parse(providerServiceListingId);
    if (filter === 'history' && listingId === undefined) {
      throw new Error('Select a service listing to review its history.');
    }
    return withRequestDeadline(
      async (requestSignal) => {
        const result = ServiceOpportunitiesPageSchema.parse(
          await getData('serviceOpportunities', {
            params: {
              status: filter,
              page: String(page),
              pageSize: String(pageSize),
              // [MCV-1850] Declined and withdrawn responses are hidden unless asked for.
              // The history view lists every status already.
              ...(filter === 'history' ? {} : { includeDeclined: String(includeDeclined) }),
              // The listing owner may decline in any open state.
              allowReject: 'true',
              ...(listingId === undefined ? {} : { providerServiceListingId: String(listingId) }),
            },
            signal: requestSignal,
          })
        );
        if (listingId !== undefined && result.data.some((row) => row.listing.id !== listingId)) {
          throw new Error('Service response listing scope changed.');
        }
        return result;
      },
      MARKET_READ_TIMEOUT_MS,
      signal
    );
  },

  /**
   * Reads every page of the inbox so the list scrolls like B2B and Job Responses.
   * Page 1 must succeed; later pages load in parallel, each with its own
   * deadline. The result is `truncated` only when rows may have been skipped: a
   * page cap, a failed page, or a row read twice because the list shifted
   * mid-read (which pushes another row past a page already read). A row that
   * merely left the filter mid-read skips nothing.
   */
  getAll: async (
    filter: OpportunityFilter,
    signal?: AbortSignal,
    includeDeclined = false
  ): Promise<ServiceInbox> => {
    const readPage = (page: number) =>
      SERVICE_OPPORTUNITIES_API.getPage(
        filter,
        page,
        signal,
        undefined,
        INBOX_PAGE_SIZE,
        includeDeclined
      );
    const first = await readPage(1);
    const pageCount = Math.max(1, Math.ceil(first.total / INBOX_PAGE_SIZE));
    const lastPage = Math.min(pageCount, MAX_INBOX_PAGES);
    const later: (ServiceOpportunitiesPage | null)[] = [];
    let next = 2;
    const worker = async () => {
      while (next <= lastPage) {
        const page = next++;
        try {
          later[page - 2] = await readPage(page);
        } catch (error) {
          if (signal?.aborted) throw error;
          later[page - 2] = null;
        }
      }
    };
    await Promise.all(Array.from({ length: INBOX_PAGE_CONCURRENCY }, worker));
    const rows = new Map<number, ServiceOpportunity>();
    let shifted = false;
    for (const page of [first, ...later])
      for (const row of page?.data ?? []) {
        if (rows.has(row.responseId)) shifted = true;
        else rows.set(row.responseId, row);
      }
    const data = [...rows.values()];
    const truncated = pageCount > MAX_INBOX_PAGES || later.some((page) => page === null) || shifted;
    return { data, total: truncated ? Math.max(first.total, data.length) : data.length, truncated };
  },

  /**
   * Background refresh of a loaded inbox: one page-1 read merged into `current`
   * instead of a full re-read. The list is ordered by last update, so a row
   * that changed is on page 1. A loaded row that page 1 now covers (ranked
   * ahead of its last row) but omits has left the filter and is dropped;
   * rows ranked after page 1 are kept as loaded.
   */
  refreshHead: async (
    filter: OpportunityFilter,
    current: ServiceInbox,
    signal?: AbortSignal,
    includeDeclined = false
  ): Promise<ServiceInbox> => {
    const first = await SERVICE_OPPORTUNITIES_API.getPage(
      filter,
      1,
      signal,
      undefined,
      INBOX_PAGE_SIZE,
      includeDeclined
    );
    if (first.data.length < INBOX_PAGE_SIZE)
      return { data: first.data, total: first.data.length, truncated: false };
    const onPage = new Set(first.data.map((row) => row.responseId));
    const last = first.data[first.data.length - 1];
    const data = [
      ...first.data,
      ...current.data.filter((row) => !onPage.has(row.responseId) && ranksAfter(row, last)),
    ];
    return {
      data,
      total: current.truncated ? Math.max(first.total, data.length) : data.length,
      truncated: current.truncated,
    };
  },

  getThread: (responseId: number, signal?: AbortSignal) =>
    withRequestDeadline(
      async (requestSignal) => {
        const options = { pathParams: { responseId: String(responseId) }, signal: requestSignal };
        const [rawOpportunity, rawMessages] = await Promise.all([
          getData('serviceOpportunity', { ...options, params: { allowReject: 'true' } }),
          getData('jobMessages', options),
        ]);
        const opportunity = ServiceOpportunitySchema.parse(rawOpportunity);
        const messages = z.array(JobMessageSchema).parse(rawMessages);
        if (
          opportunity.responseId !== responseId ||
          messages.some((message) => message.responseId !== responseId)
        ) {
          throw new Error('Service response scope changed.');
        }
        return { opportunity, messages };
      },
      MARKET_READ_TIMEOUT_MS,
      signal
    ),

  sendReply: (responseId: number, messageBody: string, signal?: AbortSignal) =>
    withRequestDeadline(
      async (requestSignal) => {
        const message = JobMessageSchema.parse(
          await postData('jobMessages', {
            pathParams: { responseId: String(responseId) },
            data: { messageBody },
            signal: requestSignal,
          })
        );
        if (message.responseId !== responseId) throw new Error('Message response scope changed.');
        return message;
      },
      MARKET_ACTION_TIMEOUT_MS,
      signal
    ),

  markRead: (responseId: number, signal?: AbortSignal) =>
    withRequestDeadline(
      async (requestSignal) => {
        await patchData('jobMessagesRead', {
          pathParams: { responseId: String(responseId) },
          signal: requestSignal,
        });
      },
      MARKET_ACTION_TIMEOUT_MS,
      signal
    ),

  withdraw: (responseId: number, signal?: AbortSignal) =>
    withRequestDeadline(
      async (requestSignal) => {
        await patchData('jobOpportunityStatus', {
          pathParams: { responseId: String(responseId) },
          data: { status: 'WITHDRAWN' },
          signal: requestSignal,
        });
      },
      MARKET_ACTION_TIMEOUT_MS,
      signal
    ),

  /** [MCV-1850] The listing owner declines; stored as REJECTED. */
  reject: (responseId: number, signal?: AbortSignal) =>
    withRequestDeadline(
      async (requestSignal) => {
        await patchData('jobOpportunityStatus', {
          pathParams: { responseId: String(responseId) },
          data: { status: 'REJECTED' },
          signal: requestSignal,
        });
      },
      MARKET_ACTION_TIMEOUT_MS,
      signal
    ),

  accept: (
    responseId: number,
    expectedNegotiationId: number,
    signal?: AbortSignal,
    // [MCV-1933] The hiring employer's authorization of the fee schedule they were shown.
    feeAuthorization?: FeeAuthorization
  ) =>
    withRequestDeadline(
      async (requestSignal) => {
        const id = z.number().int().positive().parse(expectedNegotiationId);
        const receipt = z
          .object({
            ok: z.literal(true),
            responseId: z.number().int().positive(),
            status: z.enum(['PENDING_ACCEPTANCE', 'ACCEPTED']),
            providerAccepted: z.boolean(),
            employerAccepted: z.boolean(),
            isUnmasked: z.boolean(),
          })
          .parse(
            await postData('hlxAccept', {
              pathParams: { responseId: String(responseId) },
              data: {
                expectedNegotiationId: id,
                ...(feeAuthorization ? { feeAuthorization } : {}),
              },
              signal: requestSignal,
            })
          );
        if (
          receipt.responseId !== responseId ||
          (!receipt.providerAccepted && !receipt.employerAccepted) ||
          (receipt.status === 'PENDING_ACCEPTANCE' &&
            receipt.providerAccepted &&
            receipt.employerAccepted) ||
          (receipt.status === 'ACCEPTED' &&
            !(receipt.providerAccepted && receipt.employerAccepted && receipt.isUnmasked))
        )
          throw new Error('Acceptance acknowledgement could not be verified.');
      },
      MARKET_ACTION_TIMEOUT_MS,
      signal
    ),
};
